import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Button, Form, Table } from 'react-bootstrap';
import { db as appDb } from '../../firebase/firebase';
import PracticasRegistroPanel from '../Components/PracticasRegistroPanel';

const RegistroPracticas = ({ alumnoId }) => {
  const { id: routeAlumnoId } = useParams();
  const id = alumnoId || routeAlumnoId;
  const db = appDb;

  const [evaluaciones, setEvaluaciones] = useState([]);
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroModuloEval, setFiltroModuloEval] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchEvaluaciones = async () => {
      try {
        const evalRef = collection(db, 'evaluaciones');
        const condiciones = [where('userId', '==', id)];
        if (filtroNivel) condiciones.push(where('nivel', '==', filtroNivel));
        if (filtroModuloEval) condiciones.push(where('modulo', '==', filtroModuloEval));
        const q = query(evalRef, ...condiciones);
        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setEvaluaciones(items);
        setPage(1);
      } catch (error) {
        console.error('Error fetching evaluaciones:', error);
      }
    };
    if (id) fetchEvaluaciones();
  }, [id, db, filtroNivel, filtroModuloEval]);

  const filteredEvaluaciones = evaluaciones
    .filter((ev) => !filtroTipo || (ev?.tipo || '').toLowerCase() === filtroTipo.toLowerCase())
    .filter((ev) => !filtroNombre || (ev?.nombre || '').toLowerCase().includes(filtroNombre.toLowerCase()));

  const totalPages = Math.max(1, Math.ceil(filteredEvaluaciones.length / pageSize));
  const currentItems = filteredEvaluaciones.slice((page - 1) * pageSize, page * pageSize);

  const exportCSV = () => {
    const headers = ['Fecha', 'Nivel', 'Módulo', 'Ejercicio', 'Tipo', 'Pregunta', 'Respuesta(s)'];
    const rows = filteredEvaluaciones.map((ev) => {
      const d = ev?.createdAt?.toDate ? ev.createdAt.toDate() : new Date(ev?.createdAt);
      const fecha = Number.isNaN(d.getTime()) ? '' : d.toISOString();
      const respuestasCell = ev?.respuestas
        ? Object.entries(ev.respuestas).map(([key, value]) => `${key}: ${value}`).join(' | ')
        : ev?.respuesta ?? '';
      return [
        `"${fecha}"`,
        `"${ev?.nivel || ''}"`,
        `"${ev?.modulo || ''}"`,
        `"${ev?.nombre || ''}"`,
        `"${ev?.tipo || ''}"`,
        `"${ev?.pregunta || ''}"`,
        `"${(respuestasCell || '').replace(/"/g, '""')}"`,
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evaluaciones_${id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PracticasRegistroPanel alumnoId={id} />

      <hr className="my-4" />
      <h2 className="mt-4">Resultados de Evaluaciones</h2>
      <Form className="mb-3">
        <div className="d-flex gap-3 flex-wrap">
          <Form.Group>
            <Form.Label>Filtrar por Nivel</Form.Label>
            <Form.Select value={filtroNivel} onChange={(e) => setFiltroNivel(e.target.value)}>
              <option value="">Todos</option>
              <option value="Basico">Básico</option>
              <option value="Intermedio">Intermedio</option>
              <option value="Avanzado">Avanzado</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Filtrar por Módulo</Form.Label>
            <Form.Select value={filtroModuloEval} onChange={(e) => setFiltroModuloEval(e.target.value)}>
              <option value="">Todos</option>
              {Array.from({ length: 20 }, (_, index) => (
                <option key={index + 1} value={`${index + 1}`}>{`Módulo ${index + 1}`}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Filtrar por Nombre</Form.Label>
            <Form.Control
              type="text"
              value={filtroNombre}
              placeholder="Ej. Extraiga ideas principales"
              onChange={(e) => setFiltroNombre(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Filtrar por Tipo</Form.Label>
            <Form.Select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="">Todos</option>
              <option value="Seleccion simple">Selección simple</option>
              <option value="Respuesta libre">Respuesta libre</option>
              <option value="Texto libre">Texto libre</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Tamaño de página</Form.Label>
            <Form.Select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="d-flex align-items-end">
            <Button variant="outline-secondary" onClick={exportCSV}>Exportar CSV</Button>
          </div>
        </div>
      </Form>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Nivel</th>
            <th>Módulo</th>
            <th>Ejercicio</th>
            <th>Tipo</th>
            <th>Pregunta</th>
            <th>Respuesta(s)</th>
          </tr>
        </thead>
        <tbody>
          {currentItems
            .sort((a, b) => {
              const firstDate = a?.createdAt?.toDate ? a.createdAt.toDate() : new Date(a?.createdAt);
              const secondDate = b?.createdAt?.toDate ? b.createdAt.toDate() : new Date(b?.createdAt);
              return (secondDate?.getTime?.() || 0) - (firstDate?.getTime?.() || 0);
            })
            .map((ev) => {
              const fecha = (() => {
                try {
                  const date = ev?.createdAt?.toDate ? ev.createdAt.toDate() : new Date(ev?.createdAt);
                  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
                } catch {
                  return '—';
                }
              })();
              const respuestasCell = (() => {
                if (ev?.respuestas && typeof ev.respuestas === 'object') {
                  return Object.entries(ev.respuestas).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ');
                }
                if (typeof ev?.respuesta !== 'undefined') {
                  return Array.isArray(ev.respuesta) ? ev.respuesta.join(', ') : String(ev.respuesta);
                }
                const omit = new Set(['id', 'userId', 'nombre', 'nivel', 'modulo', 'tipo', 'createdAt', 'pregunta']);
                const extras = Object.entries(ev)
                  .filter(([key]) => !omit.has(key))
                  .map(([key, value]) => {
                    if (value == null) return null;
                    if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
                    if (typeof value === 'object') {
                      try {
                        return `${key}: ${Object.entries(value).map(([innerKey, innerValue]) => `${innerKey}=${innerValue}`).join('; ')}`;
                      } catch {
                        return `${key}: ${JSON.stringify(value)}`;
                      }
                    }
                    return `${key}: ${value}`;
                  })
                  .filter(Boolean)
                  .join(' | ');
                return extras || '—';
              })();

              return (
                <tr key={ev.id}>
                  <td>{fecha}</td>
                  <td>{ev?.nivel || '—'}</td>
                  <td>{ev?.modulo || '—'}</td>
                  <td>{ev?.nombre || '—'}</td>
                  <td>{ev?.tipo || '—'}</td>
                  <td>{ev?.pregunta || '—'}</td>
                  <td>{respuestasCell}</td>
                </tr>
              );
            })}
        </tbody>
      </Table>
      <div className="d-flex justify-content-between align-items-center mt-2">
        <div>{`Página ${page} de ${totalPages}`}</div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
            Anterior
          </Button>
          <Button variant="outline-primary" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
            Siguiente
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistroPracticas;
